import React from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Tag, Users, FileText, Award, Calendar, Sparkles, BarChart3, LogOut, Database, Package, Megaphone } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const NAV = [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { to: '/admin/catalog', label: 'Catalog Scraper', icon: Database },
    { to: '/admin/products', label: 'Products', icon: Package },
    { to: '/admin/malls', label: 'Malls', icon: Building2 },
    { to: '/admin/brands', label: 'Brands', icon: Tag },
    { to: '/admin/customers', label: 'Customers', icon: Users },
    { to: '/admin/bills', label: 'Bill Approvals', icon: FileText },
    { to: '/admin/offers', label: 'Offers', icon: Sparkles },
    { to: '/admin/events', label: 'Events', icon: Calendar },
    { to: '/admin/rewards', label: 'Rewards', icon: Award },
    { to: '/admin/marketing', label: 'Marketing', icon: Megaphone },
    { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
];

export default function AdminLayout({ children }) {
    const { user, type, logout, loading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
    if (type !== 'admin') return <Navigate to="/admin/login" replace />;

    return (
        <div className="min-h-screen flex bg-ivory" data-testid="admin-layout">
            <aside className="w-64 bg-navy text-white flex flex-col fixed h-screen" data-testid="admin-sidebar">
                <div className="px-6 py-7 border-b border-white/10">
                    <Link to="/admin" className="block">
                        <div className="font-serif text-2xl text-white">INORBIT</div>
                        <div className="text-[10px] tracking-[0.3em] text-gold font-semibold mt-1">ONE • ADMIN</div>
                    </Link>
                </div>
                <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
                    {NAV.map((n) => {
                        const active = n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to);
                        return (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${active ? 'bg-white/10 text-gold' : 'text-white/70 hover:text-white hover:bg-white/5'}`}
                            >
                                <n.icon size={16} strokeWidth={1.5} />
                                {n.label}
                            </Link>
                        );
                    })}
                </nav>
                <div className="border-t border-white/10 p-4">
                    <div className="text-xs text-white/60 mb-3">
                        <div className="font-medium text-white">{user.name}</div>
                        <div className="capitalize">{user.role?.replace('_', ' ')}</div>
                    </div>
                    <button onClick={() => { logout(); navigate('/admin/login'); }} className="flex items-center gap-2 text-xs text-white/60 hover:text-gold" data-testid="admin-logout-btn">
                        <LogOut size={14} strokeWidth={1.5} /> Sign out
                    </button>
                </div>
            </aside>
            <main className="flex-1 ml-64">{children}</main>
        </div>
    );
}
